import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { stripRestockFromVariantPickers } from '@theme/cw-restock-layout';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #prefetchController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const fromSwatch = productCard?.getSelectedVariantId();
    if (fromSwatch) return fromSwatch;

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this), {
      signal: this.#cartUpdateAbortController.signal,
    });

    // Warm the cache on first hover so the modal opens near-instantly on click.
    this.addEventListener('mouseenter', this.#prefetch, {
      once: true,
      signal: this.#cartUpdateAbortController.signal,
    });

    // Prefetch as soon as the card scrolls near the viewport — fires before hover,
    // which means the cache is warm even on the very first click after page load.
    this.#observeViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#prefetchController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Starts an IntersectionObserver that fires #prefetch once the card is within
   * 200 px of the viewport. Disconnects after the first intersection so it doesn't
   * re-fire if the card scrolls out and back in.
   */
  #observeViewport() {
    if (typeof IntersectionObserver === 'undefined') return;
    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#prefetch();
            this.#viewportObserver?.disconnect();
            this.#viewportObserver = null;
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#viewportObserver.observe(this);
  }

  /**
   * Fetches and caches the product grid on first hover. Uses its own controller
   * so it doesn't interfere with the main click-triggered fetch.
   * Also kicks off background prefetches for all linked color-swatch products
   * so that switching colors in the quick view is instant.
   */
  #prefetch = () => {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url) || this.#prefetchController) return;

    this.#prefetchController = new AbortController();

    fetch(url, { signal: this.#prefetchController.signal })
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement && !this.#cachedContent.has(url)) {
          this.#cachedContent.set(url, /** @type {Element} */ (gridElement.cloneNode(true)));
          prefetchSwatchPages(gridElement);
        }
      })
      .catch(() => {})
      .finally(() => {
        this.#prefetchController = null;
      });
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // No cache — open modal immediately so the skeleton is visible while we fetch,
      // giving the user instant feedback instead of waiting for the network.
      this.#openQuickAddModal();

      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
          prefetchSwatchPages(gridElement);
        }
      }

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }
      return;
    }

    // Cache hit — morph first (fast, no visible delay), then open.
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);
    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });

    // showDialog() calls dialog.showModal() inside a rAF, then fires DialogOpenEvent.
    // At that point the dialog is in the top layer — promote the WH notification to
    // the top layer via the Popover API so it appears above the ::backdrop.
    // A second pass runs after 300 ms in case WH initialises its container lazily.
    dialogComponent.addEventListener(
      DialogOpenEvent.eventName,
      () => {
        wlhShowPopover();
        setTimeout(wlhShowPopover, 300);
        wlhSyncNotificationQuickAddState();
      },
      { once: true }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const buyButtonsBlock = productGrid.querySelector('.buy-buttons-block');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (buyButtonsBlock) {
        productGrid.appendChild(buyButtonsBlock);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    syncMfSwatchActive(modalContent, productGrid);

    initWishlistHeroInQuickAdd(modalContent);
    initSizeGuideInQuickAdd(modalContent);
    syncQuickAddInitialBuyButtonState(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.forceChoose === 'true' || this.closest('.tsw-price-row__quick-add')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #swatchFetchController = null;
  /** @type {MutationObserver | null} */
  #wlhObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#stripRestockAfterVariantUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('change', this.#handleQuickAddVariantChange, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleMfSwatchClick, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Watch for WH notification elements added to document.body while the dialog
    // is open and promote them to the top layer. Also retries after a short delay
    // because MUI portals may be added as empty divs and rendered into asynchronously.
    this.#wlhObserver = new MutationObserver((mutations) => {
      const dialog = this.querySelector('dialog');
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
      for (const { addedNodes } of mutations) {
        for (const node of addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (wlhShouldPromote(node)) {
            wlhShowPopover(node);
          } else {
            // MUI portal div may be empty when first added — check after React renders
            setTimeout(() => {
              if (dialog.open && node.isConnected && wlhShouldPromote(node)) wlhShowPopover(node);
            }, 100);
          }
        }
      }
      wlhSyncNotificationQuickAddState();
    });
    this.#wlhObserver.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#swatchFetchController?.abort();
    this.#wlhObserver?.disconnect();
    this.#wlhObserver = null;
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Intercepts metafield swatch anchor clicks inside the modal. Instantly moves the
   * active ring to the clicked swatch, then serves content from the module-level
   * swatchPageCache (populated at viewport entry alongside the main product prefetch)
   * or falls back to a live fetch when the cache is cold.
   * @param {Event} event
   */
  #handleMfSwatchClick = async (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const anchor = event.target.closest('a.variant-option__button-label');
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.contains(anchor)) return;

    event.preventDefault();

    const swatchOrder = [
      ...modalContent.querySelectorAll('fieldset.variant-option--color a.variant-option__button-label[href]'),
    ].map((a) => mfSwatchPath(/** @type {HTMLAnchorElement} */ (a).href));

    // Instant visual feedback before any network activity.
    setActiveMfSwatch(modalContent, anchor);

    const url = anchor.href;

    // Fast path: module-level cache warmed at viewport entry — zero network wait.
    let html = swatchPageCache.get(url);

    if (!html) {
      this.#swatchFetchController?.abort();
      this.#swatchFetchController = new AbortController();

      try {
        const response = await fetch(url, { signal: this.#swatchFetchController.signal });
        if (!response.ok) return;
        const text = await response.text();
        html = new DOMParser().parseFromString(text, 'text/html');
        swatchPageCache.set(url, html);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') console.error(error);
        return;
      } finally {
        this.#swatchFetchController = null;
      }
    }

    if (!html) return;

    this.#loadProductHtmlIntoModal(modalContent, url, html, { swatchOrder, activeHref: url });
  };

  /**
   * Loads a fetched product page into the quick-add modal.
   * @param {HTMLElement} modalContent
   * @param {string} url
   * @param {Document} html
   * @param {{ swatchOrder?: string[], activeHref?: string }} [options]
   */
  #loadProductHtmlIntoModal(modalContent, url, html, options = {}) {
    const sourceGrid = html.querySelector('[data-product-grid-content]');
    if (!sourceGrid) return;

    // Clone so the cached Document is never mutated by mobile re-ordering below.
    const productGrid = /** @type {Element} */ (sourceGrid.cloneNode(true));
    // Keep a second clone for keyed variant-picker morph (modal morph moves nodes out of productGrid).
    const pickerSourceGrid = /** @type {Element} */ (sourceGrid.cloneNode(true));

    if (isMobileBreakpoint()) {
      for (const grid of [productGrid, pickerSourceGrid]) {
        const productDetails = grid.querySelector('.product-details');
        const productFormComponent = grid.querySelector('product-form-component');
        const buyButtonsBlock = grid.querySelector('.buy-buttons-block');
        const variantPicker = grid.querySelector('variant-picker');
        const productPrice = grid.querySelector('product-price');

        const titleAnchor = grid.querySelector('.view-product-title a');
        const productTitle = document.createElement('a');
        productTitle.textContent = titleAnchor?.textContent?.trim() || '';
        productTitle.href = url;

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');
        productHeader.appendChild(productTitle);
        if (productPrice) productHeader.appendChild(productPrice);
        grid.appendChild(productHeader);

        if (variantPicker) grid.appendChild(variantPicker);
        if (productFormComponent) grid.appendChild(productFormComponent);
        if (buyButtonsBlock) grid.appendChild(buyButtonsBlock);

        productDetails?.remove();
      }
    }

    morph(modalContent, productGrid);

    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (variantPicker) {
      variantPicker.updateVariantPicker(pickerSourceGrid);
    }

    if (options.swatchOrder?.length) {
      restoreQuickViewSwatches(modalContent, options.swatchOrder, options.activeHref);
    } else {
      syncMfSwatchActive(modalContent, sourceGrid);
    }

    // Warm cache for sibling swatches of the newly shown product.
    prefetchSwatchPages(sourceGrid);

    initWishlistHeroInQuickAdd(modalContent);
    initSizeGuideInQuickAdd(modalContent);
    syncQuickAddInitialBuyButtonState(modalContent);
  }

  /**
   * Opens quick-add for an arbitrary product URL (e.g. wishlist add-to-bag).
   * @param {string} productPageUrl
   * @param {{ variantId?: string | number }} [options]
   */
  async openForProduct(productPageUrl, options = {}) {
    if (!productPageUrl) return;

    let url = productPageUrl;
    try {
      const parsed = new URL(productPageUrl, location.origin);
      if (options.variantId) parsed.searchParams.set('variant', String(options.variantId));
      url = parsed.toString();
    } catch {
      return;
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    this.showDialog();
    wlhShowPopover();

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.#loadProductHtmlIntoModal(modalContent, url, html);
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        console.error(error);
      }
    }
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const moreInfoLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-view-more-info-button'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (moreInfoLink) moreInfoLink.href = anchorElement.href;
  };

  #stripRestockAfterVariantUpdate = () => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    requestAnimationFrame(() => stripRestockFromVariantPickers(modalContent));
  };

  #handleQuickAddVariantChange = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.closest('variant-picker')) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.contains(event.target)) return;

    stripRestockFromVariantPickers(modalContent);

    const variantId = event.target.dataset.variantId;
    if (variantId) {
      syncQuickAddWishlistVariant(modalContent, { id: variantId });
    }
  };

  #handleDialogClose = () => {
    wlhHidePopover();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

document.addEventListener('cw:quick-add-product', (event) => {
  const detail = /** @type {{ url?: string; variantId?: string | number }} */ (event.detail ?? {});
  const dialog = document.getElementById('quick-add-dialog');
  if (dialog instanceof QuickAddDialog && detail.url) {
    dialog.openForProduct(detail.url, { variantId: detail.variantId });
  }
});

// ---------------------------------------------------------------------------
// Wishlist Hero / top-layer helpers
// ---------------------------------------------------------------------------

/**
 * Quick view: read size from data-current-checked (theme state), not :checked alone.
 * @param {Element} container
 * @returns {string | undefined}
 */
function getQuickAddSelectedVariantId(container) {
  const sizeInput = container.querySelector(
    '.variant-option--size input[data-current-checked="true"][data-variant-id], .variant-option--size input:checked[data-variant-id]'
  );
  if (sizeInput instanceof HTMLInputElement && sizeInput.dataset.variantId) {
    return sizeInput.dataset.variantId;
  }

  const variantInput = container.querySelector('input[name="id"]');
  if (variantInput instanceof HTMLInputElement && variantInput.value) {
    return variantInput.value;
  }

  return undefined;
}

/**
 * Syncs add-to-cart / sold-out on quick-view open (variant:selected normally only fires on change).
 * @param {Element} modalContent
 */
function syncQuickAddInitialBuyButtonState(modalContent) {
  stripRestockFromVariantPickers(modalContent);
  const form = modalContent.querySelector(
    'product-form-component:not(.quick-add__product-form-component)'
  );
  if (!(form instanceof HTMLElement)) return;

  const variantInput = form.querySelector('input[name="id"]');
  const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';

  /** @type {boolean | undefined} */
  let available;

  const checkedOptions = modalContent.querySelectorAll(
    'variant-picker input:checked[data-option-available]'
  );
  if (checkedOptions.length > 0) {
    available = [...checkedOptions].every(
      (input) => input instanceof HTMLInputElement && input.dataset.optionAvailable === 'true'
    );
  } else if (variantId) {
    const script = modalContent.querySelector('variant-picker script[type="application/json"]');
    if (script?.textContent) {
      try {
        const variant = JSON.parse(script.textContent);
        if (String(variant.id) === String(variantId)) {
          available = variant.available === true;
        }
      } catch {
        /* ignore malformed JSON */
      }
    }
  }

  if (available === undefined) {
    const addBtn = form.querySelector('button[name="add"]');
    if (addBtn instanceof HTMLButtonElement) {
      if (addBtn.classList.contains('add-to-cart-button--sold-out')) {
        available = false;
      } else {
        available = !addBtn.disabled;
      }
    }
  }

  if (available === undefined) return;

  const picker = modalContent.querySelector('variant-picker');
  const selected = picker?.querySelector('input:checked');
  const optionValueId =
    selected instanceof HTMLInputElement ? selected.dataset.optionValueId ?? '' : '';

  const eventTarget = picker instanceof HTMLElement ? picker : form;
  eventTarget.dispatchEvent(
    new VariantSelectedEvent({
      id: optionValueId,
      available,
    })
  );
}

/**
 * WH "product-page" listener needs PDP JSON — missing in quick view.
 * @param {Element} container
 * @param {{ id?: number | string; price?: number; featured_media?: { preview_image?: { src?: string } } }} [variant]
 */
function syncQuickAddWishlistVariant(container, variant) {
  const variantId = variant?.id ? String(variant.id) : getQuickAddSelectedVariantId(container);
  if (!variantId) return;

  container.querySelectorAll('.wishlist-hero-custom-button').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    button.removeAttribute('data-wlh-variantlistener');
    button.removeAttribute('data-wlh-variantListener');
    button.dataset.wlhVariantid = variantId;

    if (typeof variant?.price === 'number') {
      button.dataset.wlhPrice = String(variant.price / 100);
    }

    const imageSrc = variant?.featured_media?.preview_image?.src;
    if (imageSrc) {
      button.dataset.wlhImage = imageSrc;
    }

    document.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-element', { detail: button }));
  });
}

/** @param {Element} container */
function initWishlistHeroInQuickAdd(container) {
  syncQuickAddWishlistVariant(container);
}

/** CM/IN size guide — same as PDP (`size-guide-toggle.js`). */
function initSizeGuideInQuickAdd(container) {
  if (typeof window.cwInitSizeGuides === 'function') {
    window.cwInitSizeGuides(container);
  }
}

// The quick-add <dialog> uses showModal() which puts it in the browser top layer.
// Its ::backdrop renders above all regular z-index stacking, hiding the WH
// notification. Fix: use the Popover API to place the notification in the top
// layer above the backdrop. CSS in cw-wishlist-hero.css (selector [data-wlh-popover])
// resets UA popover styles so the promoted container is a transparent overlay.

const WLH_POPOVER_IDS = ['wishlist-hero-add-notification', 'wishlist-hero-notification'];
const WLH_TOAST_CLASSES = ['wlh-toast', 'wishlisthero-toast', 'wlh-notification'];
const WLH_POPOVER_ATTR = 'data-wlh-popover';

/**
 * Returns true if the element is a WH notification that should be promoted.
 * Covers: toast classes, known WH container IDs, ID containing "wishlist",
 * and MUI portal divs whose descendant is any MuiSnackbar element.
 * @param {HTMLElement} el
 */
function wlhShouldPromote(el) {
  if (el.hasAttribute(WLH_POPOVER_ATTR)) return false; // already promoted
  if (WLH_TOAST_CLASSES.some((c) => el.classList.contains(c))) return true;
  if (WLH_POPOVER_IDS.includes(el.id)) return true;
  if (el.id && /wishlist/i.test(el.id)) return true;
  if (el.querySelector('[class*="MuiSnackbar"]')) return true;
  return false;
}

/**
 * Promotes an element (or every matching direct child of body) into the
 * browser top layer via the Popover API so it renders above the quick-add
 * dialog ::backdrop. Scans ALL body children when called without args so
 * pre-existing WH containers and MUI portals are caught regardless of when
 * they were created. Marks every promoted element with WLH_POPOVER_ATTR so
 * CSS can target it uniformly.
 * @param {HTMLElement} [el]
 */
function wlhShowPopover(el) {
  if (!('showPopover' in HTMLElement.prototype)) return;
  const targets =
    el instanceof HTMLElement
      ? [el]
      : Array.from(document.body.children).filter(
          (c) => c instanceof HTMLElement && wlhShouldPromote(/** @type {HTMLElement} */ (c))
        );
  for (const target of targets) {
    if (target.hasAttribute(WLH_POPOVER_ATTR)) continue; // already promoted
    target.setAttribute(WLH_POPOVER_ATTR, '1');
    if (!target.hasAttribute('popover')) target.setAttribute('popover', 'manual');
    try {
      /** @type {HTMLElement} */ (target).showPopover();
    } catch (_) {}
    // Re-show after paint so the notification stays above the quick-add dialog top layer.
    requestAnimationFrame(() => {
      try {
        if (target.isConnected) /** @type {HTMLElement} */ (target).showPopover();
      } catch (_) {}
    });
  }
}

/** Removes every previously promoted WH element from the top layer. */
function wlhHidePopover() {
  if (!('hidePopover' in HTMLElement.prototype)) return;
  document.querySelectorAll(`[${WLH_POPOVER_ATTR}]`).forEach((el) => {
    try {
      /** @type {HTMLElement} */ (el).hidePopover();
    } catch (_) {}
    el.removeAttribute('popover');
    el.removeAttribute(WLH_POPOVER_ATTR);
  });
  wlhSyncNotificationQuickAddState();
}

const WLH_NOTIFICATION_SELECTORS =
  '#wishlist-hero-add-notification, #wishlist-hero-notification, .MuiSnackbar-root';

/** @returns {HTMLElement[]} */
function wlhGetVisibleWishlistNotifications() {
  return [...document.querySelectorAll(WLH_NOTIFICATION_SELECTORS)].filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    return el.getBoundingClientRect().height > 0;
  });
}

/** @returns {boolean} */
function wlhIsQuickAddDialogOpen() {
  const dialog = document.querySelector('#quick-add-dialog dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

/** Keep add-to-bag from receiving clicks while the WH toast overlaps the modal footer. */
function wlhSyncNotificationQuickAddState() {
  const blockBuy =
    wlhIsQuickAddDialogOpen() && wlhGetVisibleWishlistNotifications().length > 0;
  if (document.body.hasAttribute('data-cw-quick-add-wlh-notification') !== blockBuy) {
    document.body.toggleAttribute('data-cw-quick-add-wlh-notification', blockBuy);
  }
}

/**
 * @param {number} x
 * @param {number} y
 * @returns {HTMLElement | null}
 */
function wlhNotificationAtPoint(x, y) {
  for (const node of wlhGetVisibleWishlistNotifications()) {
    const rect = node.getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      return node;
    }
  }
  return null;
}

/**
 * @param {HTMLElement} notification
 * @returns {HTMLElement | null}
 */
function wlhFindViewWishlistLabel(notification) {
  const candidates = notification.querySelectorAll('a, button, [role="button"], span, p, div');
  for (const el of candidates) {
    if (!(el instanceof HTMLElement)) continue;
    const text = el.textContent?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
    if (text === 'view wishlist') return el;
  }
  return null;
}

/**
 * @param {HTMLElement} notification
 * @param {number} x
 * @param {number} y
 * @returns {boolean}
 */
function wlhIsViewWishlistClick(notification, x, y) {
  const label = wlhFindViewWishlistLabel(notification);
  if (label) {
    const rect = label.getBoundingClientRect();
    const pad = 8;
    if (x >= rect.left - pad && x <= rect.right + pad && y >= rect.top - pad && y <= rect.bottom + pad) {
      return true;
    }
  }

  for (const el of document.elementsFromPoint(x, y)) {
    if (!(el instanceof HTMLElement)) continue;
    if (!el.closest(WLH_NOTIFICATION_SELECTORS)) continue;
    const text = el.textContent?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
    if (text === 'view wishlist' || (text.includes('view wishlist') && text.length < 32)) {
      return true;
    }
  }

  return false;
}

function wlhCloseQuickAddDialogIfOpen() {
  const dialogComponent = document.getElementById('quick-add-dialog');
  if (dialogComponent instanceof QuickAddDialog) {
    dialogComponent.closeDialog();
  }
  wlhHidePopover();
  document.body.removeAttribute('data-cw-quick-add-wlh-notification');
}

function wlhOpenWishlistFromQuickAdd() {
  if (wlhIsQuickAddDialogOpen()) {
    wlhCloseQuickAddDialogIfOpen();
  }

  if (window.WishListHero_SDK?.OpenWishList) {
    window.WishListHero_SDK.OpenWishList();
    return;
  }

  const headerWishlist = document.querySelector('.action__wishlist, a[href="#hero-wishlist"]');
  if (headerWishlist instanceof HTMLElement) headerWishlist.click();
}

/**
 * @param {Event} event
 * @returns {{ x: number; y: number } | null}
 */
function wlhEventCoords(event) {
  if (event instanceof MouseEvent) {
    return { x: event.clientX, y: event.clientY };
  }
  if (event instanceof TouchEvent && event.changedTouches[0]) {
    return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
  }
  return null;
}

/**
 * Quick-add dialog sits in the browser top layer. WH toast can paint above the
 * footer but clicks still reach add-to-bag underneath — block that and open wishlist.
 * @param {Event} event
 */
function wlhHandleViewWishlistWhileQuickAddOpen(event) {
  if (!wlhIsQuickAddDialogOpen()) return;

  const coords = wlhEventCoords(event);
  if (!coords) return;

  const notification = wlhNotificationAtPoint(coords.x, coords.y);
  if (!notification) return;

  if (!wlhIsViewWishlistClick(notification, coords.x, coords.y)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  wlhOpenWishlistFromQuickAdd();
}

document.addEventListener('pointerdown', wlhHandleViewWishlistWhileQuickAddOpen, true);
document.addEventListener('click', wlhHandleViewWishlistWhileQuickAddOpen, true);

const wlhNotificationStateObserver = new MutationObserver(() => {
  if (!wlhIsQuickAddDialogOpen()) return;
  wlhSyncNotificationQuickAddState();
  wlhShowPopover();
});
wlhNotificationStateObserver.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['style', 'class', 'hidden', 'open', 'popover'],
});

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Module-level cache: url → parsed Document for linked swatch product pages.
 * Populated at viewport entry (alongside the main product prefetch) so clicks
 * inside the quick-view modal are served instantly from memory.
 * @type {Map<string, Document>}
 */
const swatchPageCache = new Map();

/** URLs currently being prefetched — prevents duplicate in-flight requests. */
const swatchInFlight = new Set();

/**
 * Background-fetches every non-active metafield swatch href found in `container`
 * and stores the parsed Document in `swatchPageCache`. Safe to call many times —
 * skips URLs that are already cached or in-flight.
 * @param {Element} container
 */
function prefetchSwatchPages(container) {
  const anchors = container.querySelectorAll(
    'a.variant-option__button-label:not(.variant-option__button-label--mf-active)'
  );
  for (const anchor of anchors) {
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) continue;
    const url = anchor.href;
    if (swatchPageCache.has(url) || swatchInFlight.has(url)) continue;

    swatchInFlight.add(url);
    fetch(url)
      .then((r) => (r.ok ? r.text() : null))
      .then((text) => {
        if (text) swatchPageCache.set(url, new DOMParser().parseFromString(text, 'text/html'));
      })
      .catch(() => {})
      .finally(() => swatchInFlight.delete(url));
  }
}

/** @param {string} href */
function mfSwatchPath(href) {
  try {
    return new URL(href, location.origin).pathname;
  } catch {
    return href;
  }
}

/** @param {Element} container @param {string[]} order @param {string} [activeHref] */
function restoreQuickViewSwatches(container, order, activeHref) {
  const fieldset = container.querySelector('fieldset.variant-option--color');
  if (!fieldset) return;

  const anchors = /** @type {HTMLAnchorElement[]} */ ([
    ...fieldset.querySelectorAll('a.variant-option__button-label[href]'),
  ]);
  const byPath = new Map(anchors.map((a) => [mfSwatchPath(a.href), a]));

  for (const path of order) {
    const anchor = byPath.get(path);
    if (anchor) fieldset.appendChild(anchor);
  }
  anchors.forEach((a) => !order.includes(mfSwatchPath(a.href)) && fieldset.appendChild(a));

  const active = activeHref && anchors.find((a) => mfSwatchPath(a.href) === mfSwatchPath(activeHref));
  if (active) setActiveMfSwatch(container, active);
}

/**
 * After a morph, re-applies `variant-option__button-label--mf-active` to the
 * swatch whose href matches the server-marked active anchor in `sourceGrid`.
 * Morph updates class attributes by DOM position, so calling this afterward
 * ensures the correct swatch always carries the active class in the live DOM.
 * @param {Element} container - The live modal content element.
 * @param {Element} sourceGrid - The freshly-fetched product grid (pre-morph source).
 */
function syncMfSwatchActive(container, sourceGrid) {
  const href = sourceGrid.querySelector('a.variant-option__button-label--mf-active')?.getAttribute('href');
  if (!href) return;

  const fieldset = container.querySelector('fieldset.variant-option--color');
  const match = fieldset && /** @type {HTMLAnchorElement[]} */ ([
    ...fieldset.querySelectorAll('a.variant-option__button-label[href]'),
  ]).find((a) => mfSwatchPath(a.href) === mfSwatchPath(href));
  if (match) setActiveMfSwatch(container, match);
}

/**
 * Moves the `variant-option__button-label--mf-active` class and `aria-current`
 * to the clicked anchor for instant visual feedback before the network request.
 * @param {Element} container
 * @param {HTMLAnchorElement} activeAnchor
 */
function setActiveMfSwatch(container, activeAnchor) {
  const ACTIVE_CLASS = 'variant-option__button-label--mf-active';
  const previous = container.querySelector(`a.${ACTIVE_CLASS}`);
  if (previous) {
    previous.classList.remove(ACTIVE_CLASS);
    previous.setAttribute('aria-current', 'false');
  }
  activeAnchor.classList.add(ACTIVE_CLASS);
  activeAnchor.setAttribute('aria-current', 'true');
}
